<template>
  <div class="user-access-form">

    <form method="POST" :action="action" @submit="submit">
      <input type="hidden" name="_method" value="PATCH">
      <input type="hidden" name="_token" :value="$csrf_token">
      <input type="hidden" name="roles" :value="selectedRoles">
      <input type="hidden" name="permissions" :value="selectedIndividualPermissions">

      <div class="row">

        <!-- Roles/permissions tabs -->
        <div class="col-md-6">
          <div class="ibox">

            <div class="ibox-title">
              <strong>Roles & Permissions</strong>
            </div>

            <div class="ibox-content">

              <!-- Roles/individual permission list switch -->
              <div>
                <div class="input-group btn-group-justified">
                  <div class="d-table-cell input-group-prepend" role="group">
                    <button :class="getTabClass('roles')" @click="tab='roles'" type="button">Roles<span :class="getCountBadgeClass('roles')">{{selectedRoles.length}}</span></button>
                  </div>
                  <div class="d-table-cell input-group-append" role="group">
                    <button :class="getTabClass('permissions')" @click="tab='permissions'" type="button">Individual permissions<span :class="getCountBadgeClass('permissions')">{{selectedIndividualPermissions.length}}</span></button>
                  </div>
                </div>
              </div>

              <!-- Roles listing -->
              <div v-show="tab === 'roles'">

                <ul class="todo-list">
                  <li v-for="role in roles" @click.prevent="toggleRole(role.id)" class="pointer">
                    <input :id="('role_' + role.id)" type="checkbox" class="custom-checkbox" v-model="selectedRoles" :value="role.id">
                    <label :for="('role_' + role.id)">
                      <span class="ml-1" :class="{'font-bold': isRoleSelected(role.id), 'text-muted': !isRoleSelected(role.id)}">{{ role.display_name }}</span>
                    </label>
                  </li>
                </ul>

              </div>

              <!-- Individual permissions listing -->
              <div v-show="tab === 'permissions'">
                <div class="input-group my-1">
                  <span class="input-group-addon fa fa-search" id="permissions-filter-addon"></span>
                  <input type="text" class="form-control" v-model="permissionFilter" placeholder="Filter permissions..." aria-describedby="permissions-filter-addon">
                </div>
                <div v-if="unUsedPermissionIds.length">
                  <ul class="todo-list">
                    <template v-for="permission in permissions" :key="permission.id">
                      <li @click.prevent="togglePermission(permission.id)" class="pointer" v-if="!permission.hide && (unUsedPermissionIds.indexOf(permission.id) > -1)">
                        <input :id="('permission_' + permission.id)" type="checkbox" class="custom-checkbox" v-model="selectedPermissions" :value="permission.id">
                        <label :for="('permission_' + permission.id)">
                          <span class="ml-1" :class="{'font-bold': isPermissionSelected(permission.id), 'text-muted': !isPermissionSelected(permission.id)}" v-html="permissionFilterHighlight(permission.display_name)"></span>
                        </label>
                        <p class="form-control-static text-muted mb-0">
                          <small>
                            {{permission.description}}
                          </small>
                        </p>
                      </li>
                    </template>
                  </ul>
                </div>
                <div v-else class="text-center mt-3">
                  All permissions granted.
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Granted permissions -->
        <div class="col-md-6">
          <div class="ibox">

            <div class="ibox-title">
              <strong>Granted Roles & Permissions</strong>
            </div>

            <div class="ibox-content">

              <strong>Roles</strong>
              <div v-if="selectedRoles.length">
                <ul class="my-2">
                  <template v-for="role in roles">
                    <li v-if="selectedRoles.indexOf(role.id) > -1">
                      {{role.display_name}}
                    </li>
                  </template>
                </ul>
              </div>
              <div v-else>
                <ul class="my-2">
                  <li><span class="text-muted font-italic">no roles granted</span></li>
                </ul>
              </div>


              <strong>Permissions</strong>
              <div v-if="grantedPermissionIds.length">
                <ul class="mt-2">
                  <template v-for="permission in permissions">
                    <li v-if="grantedPermissionIds.indexOf(permission.id) > -1">
                      <span v-if="selectedIndividualPermissions.indexOf(permission.id) > -1" class="text-navy">{{permission.display_name}}</span>
                      <span v-else>{{permission.display_name}} <span class="text-muted">[{{ getRolesForPermission(permission.id) }}]</span></span>
                    </li>
                  </template>
                </ul>
              </div>
              <div v-else>
                <ul class="mt-2">
                  <li><span class="text-muted font-italic">no permissions granted</span></li>
                </ul>
              </div>


              <hr class="hr-line-dashed" />
              <div class="form-group">
                <a :href="cancel" class="btn btn-default" data-dismiss="modal">Cancel</a>
                <button type="submit" class="btn btn-primary">Save</button>
              </div>

            </div>
          </div>
        </div>

      </div>

    </form>

  </div>
</template>

<script>
  export default {

    /**
     * Input properties.
     */
    props: {
      // Form action.
      action: {
        type: String,
        required: true
      },

      // Cancel action.
      cancel: {
        type: String,
        required: true
      },

      // All roles with permissions and "selected for user" indicator.
      roles: {
        type: Array,
        default: () => []
      },

      // All permissions and "selected for user" indicator (as individual permission).
      permissions: {
        type: Array,
        default: () => []
      }
    },

    /**
     * Variables.
     */
    data() {
      return {
        // Which roles/permissions list to display.
        tab: "roles",

        // Selected roles/permissions list elements.
        selectedRoles: [],
        selectedPermissions: [],

        // Internal validation state.
        isValid: true,

        // Permissions filter value.
        permissionFilter: ""
      }
    },

    /**
     * Functions.
     */
    methods: {
      /**
       * Init form
       */
      init() {
        this.validate();
      },

      /**
       * Submit form data
       */
      submit(e) {
        this.validate();
        if (this.isValid) {
          return true;
        } else {
          e.preventDefault();
        }
      },

      /**
       * Validate form
       */
      validate() {
        this.isValid = true;
      },

      /**
       * Highlight filter query in result permissions list.
       */
      permissionFilterHighlight(name) {
        if(this.permissionFilter.length > 0) {
          return name.replace(new RegExp('('+this.permissionFilter+')', 'ig'), '<mark>$1</mark>');
        }
        return name;
      },

      /**
       * Select role
       */
      selectRole(roleId) {
        if (!this.isRoleSelected(roleId)) {
          this.selectedRoles.push(roleId);
        }
      },

      /**
       * Unselect role
       */
      unSelectRole(roleId) {
        if (this.isRoleSelected(roleId)) {
          this.selectedRoles.splice(this.selectedRoles.indexOf(roleId), 1);
        }
      },

      /**
       * Toggle role
       */
      toggleRole(roleId) {
        if (!this.isRoleSelected(roleId)) {
          this.selectRole(roleId);
        } else {
          this.unSelectRole(roleId);
        }
      },

      /**
       * Is role selected.
       */
      isRoleSelected(roleId) {
        return this.selectedRoles.indexOf(roleId) !== -1;
      },

      /**
       * Select permission
       */
      selectPermission(permissionId) {
        if (!this.isPermissionSelected(permissionId)) {
          this.selectedPermissions.push(permissionId);
        }
      },

      /**
       * Unselect permission
       */
      unSelectPermission(permissionId) {
        if (this.isPermissionSelected(permissionId)) {
          this.selectedPermissions.splice(this.selectedPermissions.indexOf(permissionId), 1);
        }
      },

      /**
       * Toggle permission
       */
      togglePermission(permissionId) {
        if (!this.isPermissionSelected(permissionId)) {
          this.selectPermission(permissionId);
        } else {
          this.unSelectPermission(permissionId);
        }
      },

      /**
       * Is permission selected.
       */
      isPermissionSelected(permissionId) {
        return this.selectedPermissions.indexOf(permissionId) !== -1;
      },

      /**
       * Roles/permissions tab switch class.
       */
      getTabClass(item) {
        return 'btn btn-block ' + (item === this.tab ? 'btn-primary' : 'btn-white');
      },

      /**
       * Roles/permissions count badge class.
       */
      getCountBadgeClass(item) {
        return 'ml-1 badge ' + (item === this.tab ? 'badge-primary-inverse' : 'badge-primary');
      },

      /**
       * Return a csv string of roles with a given permission enabled.
       */
      getRolesForPermission(permissionId) {
        return this.roles.filter((role) => {
          return this.isRoleSelected(role.id) &&
            role.permissions.findIndex(permission => permission.id === permissionId) > -1;
        })
          .map(role => role.display_name)
          .join(', ');
      },

    },

    // Computed properties.
    computed: {

      /**
       * Get list of all granted permission ids by role/individual permission.
       */
      grantedPermissionIds: function () {
        let permissionIds = [];

        this.grantedByRolesPermissionIds.forEach((pid) => {
          permissionIds.push(pid);
        });
        this.selectedPermissions.forEach((pid) => {
          permissionIds.push(pid);
        });
        return permissionIds;
      },

      /**
       * Get list of granted permission ids by role.
       */
      grantedByRolesPermissionIds: function () {
        let permissionIds = [];
        this.roles.forEach((role) => {
          if (this.isRoleSelected(role.id)) {
            if (role.permissions) {
              role.permissions.forEach((permission) => {
                if (permissionIds.indexOf(permission.id) === -1) {
                  // Add to granted permissions.
                  permissionIds.push(permission.id);
                }
              });
            }
          }
        });
        return permissionIds;
      },

      /**
       * Get list of unused (not granted) by roles only.
       */
      unUsedPermissionIds: function () {
        let permissionIds = [];

        this.permissions.forEach((permission) => {
          if (this.grantedByRolesPermissionIds.indexOf(permission.id) === -1) {
            permissionIds.push(permission.id);
          }
        });

        return permissionIds;
      },

      /**
       * Get the selected individual permissions excluding those which already granted by roles.
       */
      selectedIndividualPermissions: function () {
        return this.selectedPermissions.filter(pid => this.grantedByRolesPermissionIds.indexOf(pid) === -1);
      }

    },

    /**
     * Watched variables.
     */
    watch: {
      permissionFilter: function (val) {
        // Filter permissions by name, if filter value is not empty.
        this.permissions.forEach((permission) => {
          permission.hide = val.length > 0 &&
            permission.display_name.search(new RegExp(val, 'i')) === -1;
        });
      },
    },

    /**
     * Component mounted event.
     */
    mounted: function() {

      // Initial roles selection.
      this.roles.forEach((role) => {
        if (role.selected) this.selectRole(role.id);
      });

      // Initial individual permissions selection.
      this.permissions.forEach((permission) => {
        if (permission.selected) this.selectPermission(permission.id);
      });
    },
  }
</script>

<style scoped>
  li.pointer { cursor: pointer; }
</style>
